"use client";

import React, { useEffect, useRef, useState } from "react";
import { Cpu, Zap, ShieldCheck, ChartBar, Globe, Bot } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const benefits = [{
  title: "Strategic Efficiency",
  description: "Automate complex workflows to drastically reduce operational latency and manual error.",
  icon: Zap
}, {
  title: "Cost Optimization",
  description: "Minimize resource wastage through intelligent, AI-driven resource allocation models.",
  icon: ChartBar
}, {
  title: "Predictive Scaling",
  description: "Adapt infrastructure dynamically to meet fluctuating market demands in real-time.",
  icon: Cpu
}, {
  title: "Data Intelligence",
  description: "Transform raw data into actionable, high-velocity business insights for smarter decisions.",
  icon: Bot
}, {
  title: "Security Compliance",
  description: "Fortify digital assets with automated threat detection and self-healing security protocols.",
  icon: ShieldCheck
}, {
  title: "Regional Agility",
  description: "Deploy localized solutions optimized specifically for East African market dynamics.",
  icon: Globe
}];
export default function IntelligentAutomationBenefits() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="intelligent-automation-benefits" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-10">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:40px_40px]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_800px_at_50%_50%,var(--primary)_0%,transparent_100%)] opacity-20" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-foreground">
            Intelligent <span className="text-primary">Automation</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Accelerate your digital transformation with our core automated capabilities.
          </p>
        </div>

        <div ref={ref} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {benefits.map((benefit, i) => {
          const Icon = benefit.icon;
          return <div key={i} data-index={i} className="group h-full">
                <Card className="h-full bg-card/40 border-border/50 backdrop-blur-sm hover:bg-card/60 hover:border-primary/30 transition-all duration-300 group-hover:shadow-[0_0_30px_-10px_var(--primary)]">
                  <CardHeader className="pb-2">
                    <div className="mb-4 inline-flex p-3 rounded-lg bg-primary/10 text-primary ring-1 ring-primary/20 group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300 w-fit">
                      <Icon className="w-6 h-6" />
                    </div>
                    <CardTitle className="text-lg font-bold uppercase tracking-wider text-accent-foreground">
                      {benefit.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-muted-foreground text-base leading-relaxed line-clamp-2">
                      {benefit.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}